import { AlertTriangle, FilePen } from 'lucide-react'
import { useAnnexes } from '../hooks'
import type { Annex } from '../types'

/**
 * Sticky banner shown above the contract tabs. Surfaces two independent signals:
 * - one or more PUBLISHED annexes (amber): "ta umowa była aneksowana N razy"
 * - an active DRAFT (slate/info): "aneks w przygotowaniu - kontynuuj"
 *
 * Both can render at once when a draft exists in addition to past published annexes.
 * Clicking a banner jumps to the Aneksy tab via the provided callback.
 */
export function AnnexBanner({
  contractId,
  onJumpToAnnexes,
}: {
  contractId: number
  onJumpToAnnexes: () => void
}) {
  const { data } = useAnnexes(contractId, false)
  const annexes = data ?? []
  const published = annexes.filter(a => a.status === 'PUBLISHED')
  const draft = annexes.find(a => a.status === 'DRAFT') ?? null

  if (published.length === 0 && !draft) return null

  const latestPublished = published
    .slice()
    .sort((a, b) => sortByPublishedAtDesc(a, b))[0]

  return (
    <div className="px-6 pt-2 flex flex-col gap-2">
      {published.length > 0 && latestPublished && (
        <button
          type="button"
          onClick={onJumpToAnnexes}
          className="flex items-center gap-3 px-3 py-2 rounded-md border border-amber-200 bg-amber-50 text-left text-sm hover:bg-amber-100 transition-colors"
        >
          <AlertTriangle size={18} className="text-amber-700 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-amber-900">
              {countLabel(published.length)}
            </div>
            <div className="text-xs text-amber-800/80 mt-0.5">
              Ostatni aneks #{latestPublished.annexNumber} •{' '}
              {latestPublished.publishedAt
                ? fmtDate(latestPublished.publishedAt)
                : '—'}
            </div>
          </div>
          <span className="text-xs text-amber-700 font-medium">Zobacz aneksy →</span>
        </button>
      )}
      {draft && (
        <button
          type="button"
          onClick={onJumpToAnnexes}
          className="flex items-center gap-3 px-3 py-2 rounded-md border border-slate-200 bg-slate-50 text-left text-sm hover:bg-slate-100 transition-colors"
        >
          <FilePen size={18} className="text-slate-600 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-slate-900">Aneks w przygotowaniu (draft)</div>
            <div className="text-xs text-slate-600 mt-0.5">
              Autor {draft.createdByName ?? `#${draft.createdByUserId}`} • utworzony {fmtDate(draft.createdAt)}
            </div>
          </div>
          <span className="text-xs text-sky-700 font-medium">Kontynuuj →</span>
        </button>
      )}
    </div>
  )
}

function countLabel(n: number): string {
  // PL fleksja: 1 raz / 2-4 razy / 5+ razy
  if (n === 1) return 'Ta umowa była aneksowana 1 raz'
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) {
    return `Ta umowa była aneksowana ${n} razy`
  }
  return `Ta umowa była aneksowana ${n} razy`
}

function sortByPublishedAtDesc(a: Annex, b: Annex): number {
  const aTime = a.publishedAt ? new Date(a.publishedAt).getTime() : 0
  const bTime = b.publishedAt ? new Date(b.publishedAt).getTime() : 0
  return bTime - aTime
}

function fmtDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleString('pl-PL', { dateStyle: 'short', timeStyle: 'short' })
}
